import React, { Component } from "react"
import PropTypes from "prop-types"
import isEqual from "lodash/isEqual"
import Highcharts from "highcharts"
import HighchartsMore from "highcharts/highcharts-more"
import SolidGauge from "highcharts/modules/solid-gauge.src"
import Exporting from "highcharts/modules/exporting"
import { translate } from "react-i18next"
import ItemNameValueShape from "../../../state/shapes/ItemNameValueShape"
import { CHARTS } from "../../../constants"
import { formatDateForDisplay } from "../../../utils/formatting-utils"

class GaugeComparisonChart extends Component {
  componentDidMount = () => {
    this.createChart()
  }

  shouldComponentUpdate = (nextProps, nextState) => {
    return !isEqual(this.props.data, nextProps.data)
  }

  componentDidUpdate = () => {
    this.createChart()
  }

  componentWillUnmount = () => {
    this.chart.destroy()
  }

  createChart = () => {
    // Set some base config settings for a comparison chart.
    const BaseGaugeDetails = {
      rowArea: 28,
      innerRadius: 90
    }

    const {
      data,
      labelText,
      singularLabelText,
      percentageBasedChart,
      t,
      reverseLegend,
      formatDate,
      titleKey,
      subtitleKey
    } = this.props

    const rowSize = BaseGaugeDetails.rowArea / data.length
    const chartOuterRadius =
      BaseGaugeDetails.innerRadius + BaseGaugeDetails.rowArea + (data.length - 1)

    // Format our labels and tooltips.
    const gaugeColours = CHARTS.getColours(data.length)
    const labelColour = gaugeColours ? gaugeColours[0] : "#FFF"
    const labelValue = data[0] ? data[0].value : ""

    const tooltipFormat =
      labelText !== null
        ? `<b>{series.name}: {point.y} ${t(labelText)}</b>`
        : "<b>{series.name}: {point.y}</b>"

    let focusText = ""
    if (singularLabelText === null) {
      focusText = labelValue + t(labelText)
    } else if (singularLabelText !== null && labelValue > 0 && labelValue < 1.1) {
      focusText = labelValue + t(singularLabelText)
    } else {
      focusText = labelValue + t(labelText)
    }

    // Function to take a data item and its index and return a
    // formatted data series.
    const convertDataToSeriesItem = (dataItem, index) => {
      const radiusStartingPoint = BaseGaugeDetails.innerRadius + index
      let innerRadius = radiusStartingPoint
      innerRadius += index * rowSize
      let outerRadius = radiusStartingPoint
      outerRadius += (index + 1) * rowSize

      let seriesName = t(dataItem.itemName)

      if (formatDate) {
        seriesName = formatDateForDisplay(dataItem.itemName)
      }

      return {
        name: seriesName,
        marker: {
          symbol: "circle",
          fillColor: gaugeColours[index]
        },
        lineWidth: 0,
        data: [
          {
            name: seriesName,
            y: dataItem.value,
            color: gaugeColours[index]
          }
        ],
        innerRadius: `${innerRadius}%`,
        radius: `${outerRadius}%`,
        showInLegend: true
      }
    }

    // Get the element into which to insert the chart
    const chartContainer = this.chartContainerRef
    // Convert the passed data into series items.
    const chartData = data.map((d, index) => {
      return convertDataToSeriesItem(d, index)
    })

    const resultIsAboveZero = data.some(d => {
      return d.value > 0
    })

    // Set up the default chart config.
    const gaugeOptions = {
      chart: {
        renderTo: chartContainer,
        height: CHARTS.standardHeight,
        type: "solidgauge",
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false
      },
      credits: {
        enabled: false
      },
      lang: {
        contextButtonTitle: t("Common.ChartDownloadOptions")
      },
      legend: {
        reversed: reverseLegend
      },
      // Defines the base size and shape of the window and gauge
      pane: {
        center: ["50%", "70%"],
        size: "85%",
        startAngle: -90,
        endAngle: 90,
        background: {
          backgroundColor: null,
          borderWidth: 0,
          innerRadius: `${BaseGaugeDetails.innerRadius}%`,
          outerRadius: `${chartOuterRadius}%`,
          shape: "arc"
        }
      },
      colors: gaugeColours,
      title: {
        text: titleKey != null && titleKey.length > 0 ? t(titleKey) : null,
        y: 15
      },
      subtitle: {
        text: subtitleKey != null && subtitleKey.length > 0 ? t(subtitleKey) : null,
        y: 30
      },
      // Specifies how to display the tick markers on the gauge
      yAxis: {
        tickWidth: 2,
        tickLength: 7,
        offset: -18,
        minorTicks: false,
        labels: {
          formatter: function() {
            const value = this.axis.defaultLabelFormatter.call(this)
            const labelFormat = labelText !== null ? value + ` ${t(labelText)}` : value

            // If no singular text given then return the label given
            if (singularLabelText === null) {
              return labelFormat
            }

            const singularLabelFormat = value + ` ${t(singularLabelText)}`

            if (value > 0 && value < 1.1) {
              return singularLabelFormat
            } else {
              return labelFormat
            }
          },
          y: 0,
          distance: -30
        },
        min: 0,
        endOnTick: true,
        max: percentageBasedChart ? 100 : !resultIsAboveZero ? 1 : null
      },
      tooltip: {
        pointFormat: tooltipFormat,
        followPointer: true
      },
      plotOptions: {
        series: {
          animation: CHARTS.animationEnabled
        },
        solidgauge: {
          dataLabels: {
            borderWidth: 0,
            useHTML: true,
            format: `<div style='text-align:center'><span style='font-size:25px;color:
            ${labelColour || "black"}'>${focusText}</span><br/>`,
            y: -35
          }
        }
      },
      exporting: {
        enabled: CHARTS.exportEnabled,
        buttons: {
          contextButton: {
            menuItems: CHARTS.exportButtons
          }
        },
        filename: titleKey != null && titleKey.length > 0 ? t(titleKey) : null
      }
    }

    // Initialise high charts more and SolidGauge as they are required
    // for this chart type.
    HighchartsMore(Highcharts)
    SolidGauge(Highcharts)
    Exporting(Highcharts)
    // Create the chart by merging our base data with the chart data.
    this.chart = new Highcharts.Chart(
      Highcharts.merge(gaugeOptions, {
        series: chartData
      })
    )
  }

  render() {
    return (
      <div
        className="chart-container col-md-12"
        ref={c => {
          this.chartContainerRef = c
        }}
      />
    )
  }
}

GaugeComparisonChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape(ItemNameValueShape)).isRequired,
  labelText: PropTypes.string,
  singularLabelText: PropTypes.string,
  percentageBasedChart: PropTypes.bool,
  reverseLegend: PropTypes.bool,
  formatDate: PropTypes.bool,
  t: PropTypes.func.isRequired,
  titleKey: PropTypes.string,
  subtitleKey: PropTypes.string
}

GaugeComparisonChart.defaultProps = {
  labelText: null,
  singularLabelText: null,
  percentageBasedChart: false,
  reverseLegend: false,
  formatDate: false,
  titleKey: "",
  subtitleKey: ""
}

export default translate()(GaugeComparisonChart)



// WEBPACK FOOTER //
// ./src/components/charts/generic/GaugeComparisonChart.jsx